import DeleteIcon from '@mui/icons-material/Delete';
import FolderIcon from '@mui/icons-material/Folder';
import UploadIcon from '@mui/icons-material/Upload';
import {
  Box, Button, Card, CardContent, CircularProgress,
  IconButton, Table, TableBody, TableCell, TableHead,
  TableRow, Tooltip, Typography,
} from '@mui/material';
import React from 'react';
import { useNotify } from 'react-admin';

interface FileEntry {
  name: string;
  size: number;
  modified: string;
}

const fmt = (bytes: number) =>
  bytes < 1024 ? `${bytes} B`
  : bytes < 1024 * 1024 ? `${(bytes / 1024).toFixed(1)} KB`
  : `${(bytes / 1024 / 1024).toFixed(1)} MB`;

const FileManagerPage: React.FC = () => {
  const notify = useNotify();
  const [files, setFiles] = React.useState<FileEntry[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [uploading, setUploading] = React.useState(false);
  const inputRef = React.useRef<HTMLInputElement>(null);

  const token = localStorage.getItem('auth') ?? '';
  const headers = { Authorization: `Bearer ${token}` };

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch('/api/files', { headers });
      if (!res.ok) throw new Error(res.statusText);
      setFiles(await res.json());
    } catch (e) {
      notify(`Laden mislukt: ${e instanceof Error ? e.message : String(e)}`, { type: 'error' });
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => { void load(); }, [load]);

  const handleUpload = async (file: File) => {
    setUploading(true);
    try {
      const form = new FormData();
      form.append('file', file);
      const res = await fetch('/api/files', { method: 'POST', headers, body: form });
      if (!res.ok) {
        const err = await res.json().catch(() => ({ error: res.statusText }));
        throw new Error(err.error ?? res.statusText);
      }
      notify('Bestand geüpload', { type: 'success' });
      await load();
    } catch (e) {
      notify(`Upload mislukt: ${e instanceof Error ? e.message : String(e)}`, { type: 'error' });
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  const handleDelete = async (name: string) => {
    try {
      const res = await fetch(`/api/files/${encodeURIComponent(name)}`, { method: 'DELETE', headers });
      if (!res.ok) throw new Error(res.statusText);
      notify('Bestand verwijderd', { type: 'success' });
      await load();
    } catch (e) {
      notify(`Verwijderen mislukt: ${e instanceof Error ? e.message : String(e)}`, { type: 'error' });
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
        <FolderIcon />
        <Typography variant='h5' fontWeight={600}>File Manager</Typography>
      </Box>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant='subtitle2' color='text.secondary' mb={1}>
            Bestanden uploaden (PNG, JPEG, BMP, GIF, SVG — max 20 MB)
          </Typography>
          <input
            ref={inputRef}
            type='file'
            accept='image/png,image/jpeg,image/bmp,image/gif,image/svg+xml'
            style={{ display: 'none' }}
            onChange={(e) => { const f = e.target.files?.[0]; if (f) void handleUpload(f); }}
          />
          <Button
            variant='contained'
            startIcon={uploading ? <CircularProgress size={14} color='inherit' /> : <UploadIcon />}
            disabled={uploading}
            onClick={() => inputRef.current?.click()}
          >
            {uploading ? 'Uploaden...' : 'Bestand uploaden'}
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardContent sx={{ p: 0 }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : files.length === 0 ? (
            <Typography color='text.secondary' sx={{ p: 3 }}>Geen bestanden gevonden.</Typography>
          ) : (
            <Table size='small'>
              <TableHead>
                <TableRow>
                  <TableCell>Bestandsnaam</TableCell>
                  <TableCell>Grootte</TableCell>
                  <TableCell>Gewijzigd</TableCell>
                  <TableCell align='right'>Acties</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {files.map((f) => (
                  <TableRow key={f.name} hover>
                    <TableCell>
                      <a
                        href={`/files/${encodeURIComponent(f.name)}`}
                        target='_blank'
                        rel='noreferrer'
                        style={{ color: 'inherit' }}
                      >
                        {f.name}
                      </a>
                    </TableCell>
                    <TableCell>{fmt(f.size)}</TableCell>
                    <TableCell>{new Date(f.modified).toLocaleString('nl-NL')}</TableCell>
                    <TableCell align='right'>
                      <Tooltip title='Verwijderen'>
                        <IconButton size='small' color='error' onClick={() => void handleDelete(f.name)}>
                          <DeleteIcon fontSize='small' />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default FileManagerPage;
